import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    ArrowLeft, Save, CheckCircle2, Archive, Plus, Trash2,
    FileUp, FileText, ChevronDown, ChevronRight, AlertTriangle,
    Edit3, X, Lock,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
    draft: "bg-white/5 border-white/20 text-white/70",
    under_review: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    published: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    archived: "bg-white/[0.03] border-white/10 text-white/40",
};

const FIELD_TYPES = [
    "text", "textarea", "number", "integer", "float",
    "date", "datetime", "time", "boolean", "yes_no",
    "single_choice", "multi_choice", "enum", "email",
    "signature", "file_upload", "computed",
];

const MIME_OPTIONS = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/heic",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export default function GeneratedFormDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formNameDirty, setFormNameDirty] = useState(null);
    const [addFieldOpen, setAddFieldOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/forms/${id}`);
            setForm(data);
            setFormNameDirty(null);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            if (e?.response?.status === 404) navigate("/forms");
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const readOnly = form?.status === "published";

    const saveFormName = async () => {
        if (formNameDirty == null || formNameDirty === form.form_name) {
            setFormNameDirty(null);
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/forms/${id}`, { form_name: formNameDirty });
            setForm(data);
            setFormNameDirty(null);
            toast.success("Form name saved");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const updateField = async (slug, patch) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/forms/${id}/fields/${slug}`, patch);
            setForm(data);
            toast.success("Field saved");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const removeField = async (slug) => {
        if (!confirm(`Remove field "${slug}" from this form?`)) return;
        setSaving(true);
        try {
            const { data } = await api.delete(`/admin/forms/${id}/fields/${slug}`);
            setForm(data);
            toast.success("Field removed");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const addField = async (newField) => {
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/forms/${id}/fields`, newField);
            setForm(data);
            setAddFieldOpen(false);
            toast.success("Field added");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const publish = async () => {
        if (!confirm(`Publish "${form.form_name}"?\n\nOnce published the form is IMMUTABLE — to edit it later you must archive it first.`)) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/forms/${id}/publish`, {
                reason: "Reviewed and approved by curator",
            });
            setForm(data);
            toast.success("Published");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const archive = async () => {
        const reason = prompt("Reason for archiving this form?", "Re-curation required");
        if (!reason) return;
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/forms/${id}/archive`, { reason });
            setForm(data);
            toast.success("Archived — fields are editable again");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    if (loading) {
        return <div className="px-8 py-8 text-white/40 text-sm">Loading form…</div>;
    }
    if (!form) {
        return <div className="px-8 py-8 text-white/40 text-sm">Form not found.</div>;
    }

    return (
        <div className="px-8 py-8" data-testid="form-detail-page">
            <Link
                to="/forms"
                data-testid="form-back-link"
                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-4 transition-colors"
            >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                Back to Generated Forms
            </Link>

            {/* Header */}
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5 mb-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                            {formNameDirty == null ? (
                                <>
                                    <h1 className="text-xl font-display tracking-tight">{form.form_name}</h1>
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => setFormNameDirty(form.form_name)}
                                            className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white"
                                            data-testid="form-name-edit-btn"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="flex items-center gap-2 flex-1">
                                    <input
                                        type="text"
                                        value={formNameDirty}
                                        onChange={(e) => setFormNameDirty(e.target.value)}
                                        data-testid="form-name-input"
                                        className="flex-1 bg-[#050505] border border-white/20 rounded px-3 py-1.5 text-lg font-display tracking-tight"
                                        autoFocus
                                    />
                                    <button onClick={saveFormName} disabled={saving} data-testid="form-name-save-btn" className="px-3 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-xs text-emerald-300">
                                        Save
                                    </button>
                                    <button onClick={() => setFormNameDirty(null)} className="px-2 py-1.5 rounded text-xs text-white/50 hover:text-white">
                                        Cancel
                                    </button>
                                </div>
                            )}
                            <span className={`px-2 py-0.5 rounded border text-xs ${STATUS_BADGE[form.status || "draft"]}`} data-testid="form-status-badge">
                                {form.status || "draft"}
                            </span>
                            {readOnly && (
                                <span className="flex items-center gap-1 text-xs text-emerald-400/80">
                                    <Lock className="w-3 h-3" strokeWidth={1.75} />
                                    immutable
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-white/50">
                            <Meta label="Visit" value={form.visit_label} mono />
                            <Meta label="CDISC" value={form.cdisc_domain || "—"} mono />
                            <Meta label="Match" value={`${form.match_strategy} · ${form.match_score ? (form.match_score * 100).toFixed(0) + "%" : ""}`} />
                            <Meta label="Protocol" value={form.protocol_number || form.protocol_id?.slice(0, 8) + "…"} mono />
                            <Meta label="Fields" value={String(form.field_count ?? form.fields?.length ?? 0)} />
                        </div>
                        {form.source_procedure && (
                            <div className="mt-3 text-xs text-white/40">
                                <span className="text-white/30">Source procedure:</span> {form.source_procedure}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {!readOnly && form.status !== "archived" && (
                            <button
                                onClick={publish}
                                disabled={saving || (form.fields || []).length === 0}
                                data-testid="form-publish-btn"
                                className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-300 text-sm"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                Publish
                            </button>
                        )}
                        {form.status !== "archived" && (
                            <button
                                onClick={archive}
                                disabled={saving}
                                data-testid="form-archive-btn"
                                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/70 hover:text-white hover:bg-white/5 text-sm"
                            >
                                <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
                                {readOnly ? "Archive to edit" : "Archive"}
                            </button>
                        )}
                    </div>
                </div>
                {readOnly && (
                    <div className="flex items-start gap-2 px-3 py-2 rounded border border-emerald-400/20 bg-emerald-400/5 text-xs text-emerald-300/90" data-testid="form-readonly-banner">
                        <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.75} />
                        <span>
                            This form is published and immutable — it is the contract with downstream eSource consumers.
                            <strong className="text-emerald-200 ml-1">Archive it</strong> to make changes.
                        </span>
                    </div>
                )}
            </div>

            {/* Fields */}
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-display text-white/80">
                    Fields <span className="text-white/40">· {form.fields?.length || 0}</span>
                </h2>
                {!readOnly && (
                    <button
                        type="button"
                        onClick={() => setAddFieldOpen(true)}
                        data-testid="form-add-field-btn"
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/15 text-xs text-white/70 hover:text-white hover:bg-white/5"
                    >
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Add field
                    </button>
                )}
            </div>

            {(form.fields || []).length === 0 ? (
                <div className="p-8 rounded-md border border-dashed border-white/10 text-center text-sm text-white/40">
                    No fields on this form yet — use <strong>Add field</strong> above to add one.
                </div>
            ) : (
                <div className="space-y-2">
                    {(form.fields || []).map((field, idx) => (
                        <FieldCard
                            key={field.slug || idx}
                            field={field}
                            readOnly={readOnly}
                            onSave={(patch) => updateField(field.slug, patch)}
                            onRemove={() => removeField(field.slug)}
                        />
                    ))}
                </div>
            )}

            {addFieldOpen && (
                <AddFieldModal
                    onClose={() => setAddFieldOpen(false)}
                    onSubmit={addField}
                />
            )}
        </div>
    );
}

function Meta({ label, value, mono }) {
    return (
        <span>
            <span className="text-white/30 mr-1">{label}</span>
            <span className={mono ? "font-mono text-white/80" : "text-white/80"}>{value}</span>
        </span>
    );
}

function FieldCard({ field, readOnly, onSave, onRemove }) {
    const [expanded, setExpanded] = useState(false);
    const [draft, setDraft] = useState(null);

    const dirty = draft !== null;
    const startEdit = () => setDraft({
        label: field.label || "",
        type: field.type || "text",
        required: !!field.required,
        cdisc_id: field.cdisc_id || "",
        cdisc_domain: field.cdisc_domain || "",
        code_list_text: Array.isArray(field.code_list) ? (field.code_list || []).join("\n") : "",
        validations_text: Array.isArray(field.validations) ? (field.validations || []).join("\n") : "",
        backup_upload: { ...(field.backup_upload || { required: false, accepts: [], reason: null }) },
    });

    const submit = () => {
        const patch = {
            label: draft.label,
            type: draft.type,
            required: draft.required,
            cdisc_id: draft.cdisc_id || null,
            cdisc_domain: draft.cdisc_domain || null,
            code_list: draft.code_list_text.split("\n").map((s) => s.trim()).filter(Boolean),
            validations: draft.validations_text.split("\n").map((s) => s.trim()).filter(Boolean),
            backup_upload: draft.backup_upload,
        };
        onSave(patch);
        setDraft(null);
        setExpanded(true);
    };

    const backupRequired = field.backup_upload?.required;

    return (
        <div className="rounded-md border border-white/10 bg-white/[0.02]" data-testid={`field-card-${field.slug}`}>
            <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.03]"
            >
                {expanded ? (
                    <ChevronDown className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} />
                ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} />
                )}
                <span className="font-mono text-xs text-white/40 shrink-0">{field.slug}</span>
                <span className="text-sm text-white truncate flex-1">{field.label || "(no label)"}</span>
                <span className="text-xs text-white/40 font-mono shrink-0">{field.type}</span>
                {field.required && (
                    <span className="text-[10px] uppercase tracking-wide text-red-400 shrink-0">req</span>
                )}
                {backupRequired && (
                    <span className="flex items-center gap-1 text-[10px] text-violet-400 shrink-0">
                        <FileUp className="w-3 h-3" strokeWidth={1.75} />
                        backup
                    </span>
                )}
                {field.cdisc_id && (
                    <span className="text-[10px] font-mono text-sky-400 shrink-0">{field.cdisc_id}</span>
                )}
            </button>

            {expanded && (
                <div className="border-t border-white/10 p-4 space-y-4">
                    {dirty ? (
                        <FieldEditor
                            draft={draft}
                            setDraft={setDraft}
                            onSubmit={submit}
                            onCancel={() => setDraft(null)}
                        />
                    ) : (
                        <>
                            <FieldDetails field={field} />
                            <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                                {!readOnly && (
                                    <>
                                        <button
                                            onClick={startEdit}
                                            data-testid={`field-edit-btn-${field.slug}`}
                                            className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-white/70 hover:text-white hover:bg-white/5"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            Edit field
                                        </button>
                                        <button
                                            onClick={onRemove}
                                            data-testid={`field-remove-btn-${field.slug}`}
                                            className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-white/50 hover:text-red-400"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            Remove field
                                        </button>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function FieldDetails({ field }) {
    const codeList = Array.isArray(field.code_list) ? field.code_list : [];
    const validations = Array.isArray(field.validations) ? field.validations : [];
    const bu = field.backup_upload || {};
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
            <div className="space-y-1.5">
                <DetailRow label="Label" value={field.label} />
                <DetailRow label="Slug" value={field.slug} mono />
                <DetailRow label="Type" value={field.type} mono />
                <DetailRow label="Required" value={field.required ? "yes" : "no"} />
                <DetailRow label="CDISC" value={field.cdisc_domain ? `${field.cdisc_domain} · ${field.cdisc_id || "—"}` : "—"} mono />
                {codeList.length > 0 && (
                    <div>
                        <div className="text-white/40 mb-1">Code list</div>
                        <ul className="text-white/80 space-y-0.5">
                            {codeList.map((c, i) => (
                                <li key={i} className="font-mono text-[11px] before:content-['•'] before:mr-1.5 before:text-white/30">{c}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {validations.length > 0 && (
                    <div>
                        <div className="text-white/40 mb-1">Validations</div>
                        <ul className="text-white/70 space-y-0.5">
                            {validations.map((v, i) => (
                                <li key={i} className="text-[11px] before:content-['•'] before:mr-1.5 before:text-white/30">{v}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            <div className="space-y-1.5 p-3 rounded border border-violet-400/15 bg-violet-400/[0.03]" data-testid={`backup-upload-card-${field.slug}`}>
                <div className="flex items-center gap-1.5 mb-1">
                    <FileUp className="w-3.5 h-3.5 text-violet-400" strokeWidth={1.75} />
                    <span className="text-xs font-medium text-violet-300">backup_upload</span>
                </div>
                <DetailRow label="Required" value={bu.required ? "yes — source doc must accompany this value" : "optional"} />
                <DetailRow
                    label="Accepts"
                    value={(bu.accepts || []).length > 0 ? (bu.accepts || []).join(", ") : "—"}
                    mono
                />
                <DetailRow label="Reason" value={bu.reason || "—"} />
            </div>
        </div>
    );
}

function DetailRow({ label, value, mono }) {
    return (
        <div className="flex items-baseline gap-2">
            <span className="text-white/40 w-20 shrink-0">{label}</span>
            <span className={`flex-1 ${mono ? "font-mono text-white/80" : "text-white/80"}`}>{value}</span>
        </div>
    );
}

function FieldEditor({ draft, setDraft, onSubmit, onCancel }) {
    const toggleAccept = (mime) => {
        setDraft((d) => {
            const cur = new Set(d.backup_upload.accepts || []);
            if (cur.has(mime)) cur.delete(mime); else cur.add(mime);
            return { ...d, backup_upload: { ...d.backup_upload, accepts: Array.from(cur) } };
        });
    };

    return (
        <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Label">
                    <input
                        type="text"
                        value={draft.label}
                        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                        data-testid="field-editor-label"
                        className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                    />
                </Field>
                <Field label="Type">
                    <select
                        value={draft.type}
                        onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                        data-testid="field-editor-type"
                        className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                    >
                        {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
                <Field label="CDISC domain">
                    <input
                        type="text"
                        value={draft.cdisc_domain}
                        onChange={(e) => setDraft({ ...draft, cdisc_domain: e.target.value.toUpperCase() })}
                        data-testid="field-editor-cdisc-domain"
                        className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5 font-mono"
                    />
                </Field>
                <Field label="CDISC id">
                    <input
                        type="text"
                        value={draft.cdisc_id}
                        onChange={(e) => setDraft({ ...draft, cdisc_id: e.target.value })}
                        data-testid="field-editor-cdisc-id"
                        className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5 font-mono"
                    />
                </Field>
            </div>
            <label className="inline-flex items-center gap-2 text-white/80">
                <input
                    type="checkbox"
                    checked={!!draft.required}
                    onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
                    data-testid="field-editor-required"
                />
                Required field
            </label>
            <Field label="Code list (one option per line)">
                <textarea
                    value={draft.code_list_text}
                    onChange={(e) => setDraft({ ...draft, code_list_text: e.target.value })}
                    rows={3}
                    data-testid="field-editor-code-list"
                    className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5 font-mono"
                />
            </Field>
            <Field label="Validations (one rule per line)">
                <textarea
                    value={draft.validations_text}
                    onChange={(e) => setDraft({ ...draft, validations_text: e.target.value })}
                    rows={2}
                    data-testid="field-editor-validations"
                    className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                />
            </Field>

            <div className="p-3 rounded border border-violet-400/15 bg-violet-400/[0.03] space-y-2">
                <div className="flex items-center gap-1.5">
                    <FileUp className="w-3.5 h-3.5 text-violet-400" strokeWidth={1.75} />
                    <span className="font-medium text-violet-300">Backup upload</span>
                </div>
                <label className="inline-flex items-center gap-2 text-white/80">
                    <input
                        type="checkbox"
                        checked={!!draft.backup_upload.required}
                        onChange={(e) => setDraft({
                            ...draft,
                            backup_upload: { ...draft.backup_upload, required: e.target.checked },
                        })}
                        data-testid="field-editor-backup-required"
                    />
                    Source document required with this value
                </label>
                <div>
                    <div className="text-white/40 mb-1">Accepts (MIME)</div>
                    <div className="flex flex-wrap gap-1.5">
                        {MIME_OPTIONS.map((mime) => {
                            const active = (draft.backup_upload.accepts || []).includes(mime);
                            return (
                                <button
                                    key={mime}
                                    type="button"
                                    onClick={() => toggleAccept(mime)}
                                    data-testid={`field-editor-mime-${mime}`}
                                    className={`px-2 py-0.5 rounded border text-[10px] font-mono transition-colors ${
                                        active
                                            ? "border-violet-400/50 bg-violet-400/15 text-violet-300"
                                            : "border-white/15 text-white/50 hover:text-white"
                                    }`}
                                >
                                    {mime.split("/")[1] || mime}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <Field label="Reason / hint to the data-entry user">
                    <input
                        type="text"
                        value={draft.backup_upload.reason || ""}
                        onChange={(e) => setDraft({
                            ...draft,
                            backup_upload: { ...draft.backup_upload, reason: e.target.value },
                        })}
                        data-testid="field-editor-backup-reason"
                        className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                    />
                </Field>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
                <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-white/60 hover:text-white">
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onSubmit}
                    data-testid="field-editor-save-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300"
                >
                    <Save className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Save field
                </button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-white/40 mb-1">{label}</div>
            {children}
        </label>
    );
}

function AddFieldModal({ onClose, onSubmit }) {
    const [slug, setSlug] = useState("");
    const [label, setLabel] = useState("");
    const [type, setType] = useState("text");
    const [required, setRequired] = useState(false);
    const [backupRequired, setBackupRequired] = useState(false);

    const submit = (e) => {
        e?.preventDefault();
        if (!slug.trim() || !label.trim()) {
            toast.error("Slug and label are required");
            return;
        }
        onSubmit({
            slug: slug.trim(),
            label: label.trim(),
            type,
            required,
            code_list: [],
            validations: [],
            backup_upload: {
                required: backupRequired,
                accepts: backupRequired ? ["application/pdf", "image/jpeg", "image/png"] : [],
                reason: backupRequired ? "Source document is required for this field" : null,
            },
        });
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="add-field-modal">
            <div className="w-full max-w-md rounded-lg border border-white/15 bg-[#0a0a0a]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <h2 className="text-sm font-medium">Add field</h2>
                    <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="add-field-close">
                        <X className="w-4 h-4 text-white/60" strokeWidth={1.5} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3 text-xs">
                    <Field label="Slug (snake_case identifier)">
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            data-testid="add-field-slug"
                            className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5 font-mono"
                            autoFocus
                        />
                    </Field>
                    <Field label="Label (visible to data-entry user)">
                        <input
                            type="text"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            data-testid="add-field-label"
                            className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                        />
                    </Field>
                    <Field label="Type">
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            data-testid="add-field-type"
                            className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                        >
                            {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <label className="inline-flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={required}
                            onChange={(e) => setRequired(e.target.checked)}
                            data-testid="add-field-required"
                        />
                        Required
                    </label>
                    <label className="inline-flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={backupRequired}
                            onChange={(e) => setBackupRequired(e.target.checked)}
                            data-testid="add-field-backup-required"
                        />
                        Backup upload required (source doc)
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-white/60 hover:text-white">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="add-field-submit"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#7C3AED] hover:bg-[#6D28D9]"
                        >
                            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Add field
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
